import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Icon } from '@rbilabs/components-library';
import { Link } from 'react-router';

import { useLocalStorageState } from 'hooks/use-local-storage-state';
import { scrollSectionIntoView } from 'pages/main-menu/direct-menu-access/utils';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { StorageKeys } from 'utils/local-storage';

import { TabbedHeaderSkeleton } from './tabbed-header.skeleton';
import { NewTabItem, NewTabWrapper, TabItem, Wrapper } from './tabbed-header.styled';
import { ITabbedHeaderProps } from './types';
import { useTabbedHeaderAnalytics } from './use-tabbed-header-analytics';

const DELAY_IN_MS = 800;

export const TabbedHeader: FC<ITabbedHeaderProps> = ({
  activeTab: newActiveTab,
  data,
  loading,
  onOpenAllCategories,
  setAsActiveTab,
}) => {
  const [activeTab, setActiveTab] = useState<number | undefined>(-1);
  const [isClickBehaviour, setIsClickBehaviour] = useState(false);
  const logTabSelected = useTabbedHeaderAnalytics();

  const enableDirectAccesToMenu = useFlag(LaunchDarklyFlag.ENABLE_DIRECT_ACCESS_TO_MENU);

  const TabWrapper = enableDirectAccesToMenu ? NewTabWrapper : Wrapper;
  const TabLinkComponent = enableDirectAccesToMenu ? NewTabItem : TabItem;

  const tabbedHeaderRef = useRef<HTMLDivElement>(null);

  const [prevActiveTab, setPrevActiveTab] = useLocalStorageState<number | null>({
    key: StorageKeys.TABBED_MENU_PREVIOUS_ACTIVE_TAB,
    defaultReturnValue: null,
  });

  const handleTabClick = (label: string, tabId: string) => {
    logTabSelected(label, tabId);
    setIsClickBehaviour(true);

    if (enableDirectAccesToMenu) {
      scrollSectionIntoView(tabId);
    }

    const idx = data?.findIndex(item => item.tabId === tabId) ?? activeTab;
    setActiveTab(idx);

    if (setAsActiveTab) {
      setAsActiveTab(tabId);
    }

    /**
     * This timeout is needed so the tabs doesn't change while the smooth
     * scroll animation is in progress. flushSync doesn't work in this
     * case since it reenders the component more than one time
     */
    setTimeout(() => {
      setIsClickBehaviour(false);
    }, DELAY_IN_MS);
  };

  useEffect(() => {
    if (prevActiveTab && typeof newActiveTab !== 'undefined' && newActiveTab < 0) {
      setActiveTab(prevActiveTab);
    } else if (!isClickBehaviour) {
      setActiveTab(newActiveTab);
    }

    if (typeof newActiveTab !== 'undefined' && newActiveTab >= 0) {
      setPrevActiveTab(newActiveTab);
    }
  }, [prevActiveTab, newActiveTab, setPrevActiveTab, isClickBehaviour]);

  useLayoutEffect(() => {
    const childOffset = enableDirectAccesToMenu ? 1 : 0;

    if (tabbedHeaderRef?.current && activeTab !== undefined) {
      const parent = tabbedHeaderRef.current;
      const element = tabbedHeaderRef.current.children[activeTab + childOffset];
      if (element instanceof HTMLElement) {
        const { left, right } = element.getBoundingClientRect();
        const containerWidth = parent.offsetWidth;

        const offsetLeft = activeTab === 0 ? 0 : element.offsetLeft;

        if (left < 1 || left > containerWidth || right > containerWidth) {
          // Old javascript contexts do not support `scrollTo`
          parent.scrollTo?.({ left: offsetLeft, behavior: 'smooth' });
        }
      }
    }
  }, [activeTab, tabbedHeaderRef, enableDirectAccesToMenu]);

  if (loading) {
    return <TabbedHeaderSkeleton />;
  }

  if (!data?.length) {
    return null;
  }

  return (
    <TabWrapper data-testid="tabbed-menu-header" ref={tabbedHeaderRef}>
      {enableDirectAccesToMenu && (
        <NewTabItem onClick={onOpenAllCategories}>
          <Icon
            width="20px"
            height="20px"
            icon="listAlt"
            title="Toggle menu"
            color="background-chip"
          />
        </NewTabItem>
      )}

      {data?.map(({ label, to, tabId }, i) => {
        if (enableDirectAccesToMenu) {
          return (
            <TabLinkComponent
              key={tabId}
              active={i === activeTab}
              data-testid={`tab-${tabId}`}
              onClick={() => handleTabClick(label, tabId)}
            >
              {label}
            </TabLinkComponent>
          );
        }

        return (
          <TabLinkComponent
            to={to}
            as={Link}
            key={label}
            active={i === activeTab}
            data-testid={`tab-${tabId}`}
            onClick={() => handleTabClick(label, tabId)}
          >
            {label}
          </TabLinkComponent>
        );
      })}
    </TabWrapper>
  );
};
